import React, { memo, useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { SelectMenu, Tooltip } from 'evergreen-ui';
import cn from 'classnames';
import { SelectArrowIcon, InfoSmallIcon } from 'public/icons';
import useWindowDimensions from 'hooks/useWindowDimensions';
import styles from './styles.module.scss';


const SELECT_ARROW_ICON_WIDTH = 32;

const MultiSelect = ({
  options,
  selected,
  onSelect,
  onDeselect,
  label,
  description,
  placeholder,
  filterPlaceholder,
  isMultiSelect,
  disabled,
  className,
  error,
  hasFilter,
  itemRenderer,
  width,
  infoContent,
  rightAction,
}) => {
  const { isDesktopWidth } = useWindowDimensions();
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [selectOptionWidth, setSelectOptionWidth] = useState(null);
  const selectRef = useRef(null);

  const isInvalid = useMemo(() => !!error, [error]);

  const selectedArray = useMemo(() => {
    if (selected && typeof selected !== 'undefined') {
      return Array.isArray(selected) ? selected : [selected];
    }

    return null;
  }, [selected]);

  const placeholderValue = placeholder || 'Select';

  const selectedValues = useMemo(
    () => (selectedArray?.length
      ? selectedArray.reduce((acc, item, index) => {
        const selectedItem = options.find((option) => option.value === item);

        const selectedItemLabel = selectedItem?.selectedLabel || selectedItem?.label;

        if (acc.length > 30) {
          return acc.includes('...') ? acc : `${acc}...`;
        }

        if (index === 0) {
          return selectedItemLabel;
        }

        return `${acc}, ${selectedItemLabel}`;
      }, '')
      : placeholderValue),
    [placeholderValue, selectedArray, options],
  );

  // Effect to set the width of the select options to be the same as the select div container
  useEffect(() => {
    if (selectRef.current) {
      const rect = selectRef.current.getBoundingClientRect();
      setSelectOptionWidth(`${rect.width - SELECT_ARROW_ICON_WIDTH}px`);
    }
  }, []);

  return (
    <SelectMenu
      isMultiSelect={isMultiSelect}
      options={options}
      selected={selectedArray}
      onSelect={onSelect}
      onDeselect={onDeselect}
      hasTitle={false}
      hasFilter={hasFilter}
      filterPlaceholder={filterPlaceholder}
      className={styles.multiSelect}
      closeOnSelect={!isMultiSelect}
      itemRenderer={itemRenderer}
      // Width need for multiselect in modal window
      width={width || (isDesktopWidth ? (selectOptionWidth || '300px') : `calc(100% - ${SELECT_ARROW_ICON_WIDTH}px)`)}
    >
      <div className={cn(styles.targetContainer, className, { [styles.disabled]: disabled })}>
        <div className={styles.labelAndAction}>
          {label && (
            <div className={styles.labelWrapper}>
              <p className={cn(styles.label, {
                [styles.error]: isInvalid,
                [styles.disabled]: disabled,
              })}
              >
                {label}
              </p>

              {infoContent && (
              /* TODO: move to common components and do this for all tooltips */
                !isDesktopWidth ? (
                  <Tooltip
                    content={infoContent}
                    isShown={isTooltipVisible}
                  >
                    <div
                    /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */
                      tabIndex="0"
                      onBlur={() => {
                        setIsTooltipVisible(false);
                      }}
                    >
                      <InfoSmallIcon onClick={(e) => {
                        e.stopPropagation();
                        setIsTooltipVisible(!isTooltipVisible);
                      }}
                      />
                    </div>
                  </Tooltip>
                ) : (
                  <Tooltip
                    content={infoContent}
                  >
                    <div>
                      <InfoSmallIcon
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      />
                    </div>
                  </Tooltip>
                )
              )}
            </div>
          )}
          {rightAction && (
            <div className={styles.rightAction}>
              {rightAction}
            </div>
          )}
        </div>
        {description && (
          <p className={styles.description}>
            {description}
          </p>
        )}

        <div 
          ref={selectRef}
          className={cn({
            [styles.select]: true,
            [styles.error]: isInvalid,
            [styles.disabled]: disabled,
          })}
        >
          <p className={cn({
            [styles.placeholder]: selectedValues === placeholderValue,
            [styles.disabled]: disabled,
          })}
          >
            {selectedValues}
          </p>

          <div className={styles.arrowDown}>
            <SelectArrowIcon />
          </div>
        </div>

        {error && <p className={styles.errorMessage}>{error}</p>}
      </div>
    </SelectMenu>
  );
};

MultiSelect.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  })).isRequired,
  selected: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  onSelect: PropTypes.func.isRequired,
  onDeselect: PropTypes.func,
  itemRenderer: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  filterPlaceholder: PropTypes.string,
  className: PropTypes.string,
  isMultiSelect: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  hasFilter: PropTypes.bool,
  description: PropTypes.string,
  width: PropTypes.string,
  infoContent: PropTypes.node,
  rightAction: PropTypes.node,
};

MultiSelect.defaultProps = {
  selected: [],
  label: null,
  placeholder: null,
  filterPlaceholder: undefined,
  className: null,
  isMultiSelect: true,
  disabled: false,
  error: '',
  hasFilter: true,
  itemRenderer: undefined,
  description: null,
  width: undefined,
  infoContent: null,
  rightAction: null,
};

export default memo(MultiSelect);
